/**
 * ReportBand — the shared informational band, spec 092.
 *
 * One slot, between the report title and the query card, for messages that
 * DESCRIBE the state of what you are looking at rather than let you change it.
 * The Figma has no such slot (it predates most of these messages), so this is us
 * extending its language rather than reproducing it.
 *
 * It exists because five shipped surfaces each rolled their own coloured div:
 *   - the saved-result banner ("Saved result — as of …")            → stale
 *   - the saved-filters notice + Reset                              → info
 *   - CB permission refusals                                        → warn
 *   - CB's "this report is too big, we'll email it to you" message  → info
 *   - the filtered-totals explainer                                 → info
 *
 * That last group is why this is worth a component and not a Tailwind snippet.
 * CC-50312: CB returns a permission refusal on the CSV path as a ~100-byte
 * human-readable body where CSV was expected, and we parsed the sentence as a
 * header row — rendering a refusal as a one-column report named after the error.
 * A message has somewhere to GO now, so "render it as data" stops being the
 * path of least resistance.
 *
 * Deliberately not a generic <Alert>: it is fixed-width to the content column,
 * it never stacks (callers render at most one), and it carries at most one
 * action plus a dismiss. A general-purpose alert would invite a stack of them.
 */
import { useId } from 'react'

/*
 * Colours come from the rc-* tokens, never inline hex — see index.css for the
 * measured contrast on each. `info` reuses the filter chip's blue on purpose:
 * in this palette blue already means "applied / active", and a band saying
 * "these filters are applied" is the same idea at a different scale.
 */
const VARIANTS = {
  info: {
    box: 'bg-rc-chip-bg border-rc-chip-line text-rc-chip-text',
    // Not the ⚠ glyph: an applied-filters notice is not a warning, and users
    // read a warning triangle on a financial report as "your numbers are wrong".
    glyph: 'ⓘ', // ⓘ
  },
  stale: {
    box: 'bg-rc-stale-bg border-rc-stale-line text-rc-stale-text',
    /*
     * R3 (2026-08-28): was byte-identical to `info`'s ⓘ, so the glyph slot
     * carried NO variant information and the only thing separating a stale band
     * from an info band on screen was its colour — which is the shape SC 1.4.1
     * is about, even though the prose is what actually satisfies the criterion
     * here. ↻ rather than ⚠ for the same reason `info` avoids ⚠: both stale
     * callers ("Saved result — as of …", "Filters were reset …") describe
     * figures that are correct but not current, and a warning triangle on a
     * financial report reads as "your numbers are wrong". ↻ says re-run, which
     * is exactly what both bands ask for.
     */
    glyph: '↻',
  },
  warn: {
    box: 'bg-rc-stale-bg border-rc-stale-line text-rc-stale-text',
    glyph: '⚠', // ⚠ — reserved for "this did not do what you asked"
  },
  /*
   * `running` — Himaad's Section 1b band, the in-flight state of a report run.
   *
   * Reuses `info`'s chip blue rather than introducing a colour: blue already
   * means "applied / active" in this palette, and a run in flight is the most
   * active thing on the screen. Deliberately NOT `stale` or `warn` — a spinner
   * on an amber ground reads as "something went wrong", which is the one thing
   * a healthy 15-second run must not say.
   *
   * A SPINNER, not a glyph, and that is a real distinction rather than a
   * decorative one: every other variant describes a state that is finished and
   * static, so a static character is honest for them. This one describes work
   * that is still happening, and motion is the only part of the band that keeps
   * saying so once the reader has read the sentence. `motion-reduce:animate-none`
   * honours prefers-reduced-motion — the elapsed clock beside it still ticks, so
   * no information lives only in the animation (SC 1.4.1 / 2.3.3).
   */
  running: {
    box: 'bg-rc-chip-bg border-rc-chip-line text-rc-chip-text',
    glyph: null,
    spinner: true,
  },
}

const ReportBand = ({
  variant = 'info',
  children,
  action,
  onDismiss,
  // `status` is polite and is right for almost everything here. A refusal is the
  // exception: the user asked for data and did not get it, so it may not wait for
  // the next natural pause in the screen reader's queue.
  role = 'status',
  className = '',
  'data-testid': testId,
}) => {
  const labelId = useId()
  const v = VARIANTS[variant] || VARIANTS.info

  if (!children) return null

  return (
    <div
      role={role}
      /*
       * R3 (2026-08-28): CONDITIONAL on there being a role.
       *
       * ARIA-in-HTML forbids aria-labelledby on a generic element, so when a
       * caller passes `role={null}` — ReportView's stale band does, so that the
       * always-mounted wrapper stays the single live region — the attribute was
       * being ignored by the accessibility tree while still LOOKING like the
       * band's naming mechanism to the next reader. Nothing observable changes
       * for the roled callers, which is the point: the attribute is now present
       * exactly where it is honoured.
       */
      aria-labelledby={role ? labelId : undefined}
      data-print-hide
      data-testid={testId}
      className={`flex flex-wrap items-center gap-x-3 gap-y-2 rounded-rc-card border px-4 py-2.5 text-sm print:hidden ${v.box} ${className}`}
    >
      {v.spinner ? (
        <span
          aria-hidden="true"
          /* `currentColor` on three sides and transparent on the fourth: the ring
             inherits the band's own text colour, which index.css already records
             as 6.2:1 on this ground, so the spinner cannot drift out of contrast
             when the token moves. */
          className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent motion-reduce:animate-none"
        />
      ) : (
        <span aria-hidden="true" className="font-semibold leading-none">{v.glyph}</span>
      )}
      <span id={labelId} className="min-w-[16rem] flex-1">{children}</span>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          /* Underlined rather than a button shell: the designer's rule is that
             ghost carries Cancel and Clear-all, and this is that tier. Inherits
             the band's own text colour, which is already AA on the band. */
          className="font-medium underline decoration-1 underline-offset-2 hover:no-underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current"
        >
          {action.label}
        </button>
      )}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          /* An icon-only control needs a real name, and "Dismiss" alone is not
             one when several bands could exist across a session. */
          aria-label={action?.dismissLabel || 'Dismiss this message'}
          className="rounded px-1 text-base leading-none opacity-70 hover:opacity-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current"
        >
          {'×'}
        </button>
      )}
    </div>
  )
}

export default ReportBand
